import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoltLightning, faEraser, faPlus } from '@fortawesome/pro-light-svg-icons';
import { Button } from '@/components/forms/ui/button';
import { PacingChart } from '@/features/line-items/pacing-chart';
import { PriceAllocationProgress } from '@/features/line-items/price-allocation-progress';
import { cumulativeSpend } from 'shared/src/domain/line-item/pacing-helpers';
import { PacingBlocksGrid } from './pacing-blocks-grid';
import { PacingConfig } from '@/features/line-items/line-item-form/ui/pacing';
import { Dispatch } from 'react';
import { CommonAction } from '@/features/line-items/line-item-form/common/form-reducer';

type Props = {
  config: PacingConfig;
  dispatch: Dispatch<CommonAction>;
  errorFor?: (field: string) => string | undefined;
  warningFor?: (field: string) => string | undefined;
};

export function CustomPacingField({ config, dispatch, errorFor, warningFor }: Props) {
  const blocks = config.rows.map(r => r.block);
  const allocatedValue = config.allocation.allocated ?? 0;
  const totalValue = config.allocation.total ?? 0;

  function onSeedMonthlyBlocks() {
    dispatch({ type: 'SEED_MONTHLY_BLOCKS' });
  }

  function onClearBlocks() {
    dispatch({ type: 'CLEAR_PACING_BLOCKS' });
  }

  function onAddBlock() {
    dispatch({ type: 'PACING_ADD_BLOCK' });
  }

  return (
    <div className="w-full min-w-0 space-y-4">
      <div className="w-full min-w-0 px-2">
        <PacingChart
          key={`${config.pacing_type}-${blocks.length}`}
          data={cumulativeSpend(config.dates, blocks)}
          blocks={blocks}
        />
      </div>
      <div>
        <PriceAllocationProgress
          total={totalValue}
          allocated={allocatedValue}
          label={config.label}
        />
        {errorFor?.('pacing_details') ? (
          <span className="text-destructive text-sm">{errorFor('pacing_details')}</span>
        ) : null}
      </div>
      <div>
        <PacingBlocksGrid
          config={config}
          dispatch={dispatch}
          errorFor={errorFor}
          warningFor={warningFor}
        />
      </div>
      {config.pacing_type === 'lifetime' ? null : (
        <div className="flex w-full gap-3">
          {config.rows.length === 0 ? (
            <Button
              variant="outline"
              type="button"
              onClick={onSeedMonthlyBlocks}
              className="flex flex-1">
              <FontAwesomeIcon icon={faBoltLightning} />
              <div>Start from monthly blocks</div>
            </Button>
          ) : (
            <Button variant="outline" type="button" onClick={onClearBlocks} className="flex flex-1">
              <FontAwesomeIcon icon={faEraser} />
              <div>Clear</div>
            </Button>
          )}
          <Button variant="outline" type="button" onClick={onAddBlock} className="flex flex-1">
            <FontAwesomeIcon icon={faPlus} />
            <div>Add pacing block</div>
          </Button>
        </div>
      )}
    </div>
  );
}
